import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamSeatControls, { type TeamContext } from './TeamSeatControls';
import type { BandV2, CandidateAnalysisDetailV2, CandidateAPIV2, SupportLevelV2 } from '../types';
import {
  fetchAnalysisStatusV2,
  fetchCandidateAnalysisDetailV2,
  removeCandidateV2,
  runCandidateAnalysisV2,
} from '../api/v2/candidates';

interface Props {
  candidate: CandidateAPIV2;
  tenderId: string;
  /** Called after a successful analysis run so the parent can refetch. */
  onAnalyzed?: () => void;
  /** Called after the candidate is removed so the parent can refetch. */
  onRemoved?: () => void;
  /**
   * Team context for this candidate at the currently selected role
   * (team_proposal_spec §6). Absent on single-position tenders and on the
   * tender-wide list, where there is no seat to hold.
   */
  team?: TeamContext;
}

const TOP_SKILLS_VISIBLE = 5;

// Fit-band badge styling (matching_model.md §5). The post-analysis ranking axis.
const BAND_STYLES: Record<BandV2, { label: string; cls: string }> = {
  COMPELLING: { label: 'Compelling', cls: 'bg-green-100 text-green-800 border-green-300' },
  CREDIBLE: { label: 'Credible', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  ARGUABLE: { label: 'Arguable', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  ELIMINATED: { label: 'Dismissed', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

// Poll cadence for a running analysis. The full run is tens of seconds to a
// couple of minutes; 3s × 100 gives a ~5-minute ceiling before we surface a
// retry (the work keeps running server-side regardless).
const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_ATTEMPTS = 100;

const AVATAR_PALETTE = [
  'bg-brand-100 text-brand-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-blue-100 text-blue-700',
  'bg-pink-100 text-pink-700',
];

/** Two-letter initials from a person's name, e.g. "Hossam Alkafri" → "HA". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic palette pick from a string — same name always gets the same color. */
function avatarPalette(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export default function CandidateCard({ candidate, tenderId, onAnalyzed, onRemoved, team }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(false);
  const [progress, setProgress] = useState<{ ruled: number; total: number } | null>(null);
  const [removing, setRemoving] = useState(false);

  // Per-criterion detail, fetched lazily the first time a verdict chip is
  // hovered, so the chip can list the criteria behind its count. Cached for the
  // life of the card; one fetch per candidate regardless of how many chips are
  // hovered.
  const [detail, setDetail] = useState<CandidateAnalysisDetailV2 | null>(null);
  const detailFetchRef = useRef(false);

  // Guard against state updates after unmount (the parent refetch can swap us out).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Fetch the per-criterion detail once, on first chip hover. Subsequent hovers
  // (and re-hovers) reuse the cached result.
  const ensureDetail = async () => {
    if (detailFetchRef.current) return;
    detailFetchRef.current = true;
    try {
      const data = await fetchCandidateAnalysisDetailV2(tenderId, candidate.id);
      if (mountedRef.current && data) setDetail(data);
      else detailFetchRef.current = false; // allow a retry if it came back empty
    } catch {
      detailFetchRef.current = false; // allow a retry on transient failure
    }
  };

  // Trigger the async run (202), then poll the status endpoint until the
  // backend reports done — the run is tens of seconds to minutes, so there's no
  // single round-trip to await. On a 404, repeated poll failure, or hitting the
  // attempt ceiling, flip to a retry; the work is idempotent, so retrying
  // resumes from whatever already persisted.
  const handleAnalyze = async () => {
    if (analyzing) return;
    setAnalyzing(true);
    setAnalyzeError(false);
    setProgress(null);
    try {
      const accepted = await runCandidateAnalysisV2(tenderId, candidate.id);
      if (!accepted) {
        if (mountedRef.current) setAnalyzeError(true);
        return;
      }
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!mountedRef.current) return;
        const status = await fetchAnalysisStatusV2(tenderId, candidate.id);
        if (mountedRef.current && status) {
          setProgress({ ruled: status.ruled, total: status.total });
        }
        if (status?.done) {
          // Findings changed — drop the cached detail so the next chip hover
          // refetches the fresh per-criterion breakdown.
          detailFetchRef.current = false;
          if (mountedRef.current) setDetail(null);
          onAnalyzed?.();
          return;
        }
      }
      // Ceiling hit — the run may still be going server-side; let the user retry.
      if (mountedRef.current) setAnalyzeError(true);
    } catch {
      if (mountedRef.current) setAnalyzeError(true);
    } finally {
      if (mountedRef.current) setAnalyzing(false);
    }
  };

  // Cross the candidate off the tender list. The parent refetch (onRemoved)
  // drops this card from the main list and surfaces it in the removed list.
  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      await removeCandidateV2(tenderId, candidate.id);
      onRemoved?.();
    } catch {
      if (mountedRef.current) setRemoving(false);
    }
    // No success reset: the card is about to be unmounted by the parent refetch.
  };

  const matched = candidate.skills?.matched ?? [];
  const visibleSkills = expanded ? matched : matched.slice(0, TOP_SKILLS_VISIBLE);
  const hiddenCount = Math.max(0, matched.length - TOP_SKILLS_VISIBLE);
  const percent = Math.round((candidate.skills?.score ?? 0) * 100);

  const analysis = candidate.analysis;
  const pendingCount = analysis ? Math.max(0, analysis.total - analysis.ruled) : 0;
  const verdictChips: {
    key: string;
    verdict: SupportLevelV2;
    n: number;
    label: string;
    cls: string;
  }[] = analysis
    ? [
        { key: 'strong', verdict: 'STRONG' as const, n: analysis.strong, label: 'strong', cls: 'bg-green-50 text-green-700 border-green-200' },
        { key: 'solid', verdict: 'SOLID' as const, n: analysis.solid, label: 'solid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        { key: 'thin', verdict: 'THIN' as const, n: analysis.thin, label: 'thin', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
        { key: 'unsupported', verdict: 'UNSUPPORTED' as const, n: analysis.unsupported, label: 'unsupported', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
        { key: 'needs_clarifying', verdict: 'NEEDS_CLARIFYING' as const, n: analysis.needs_clarifying, label: 'needs clarifying', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
      ].filter((v) => v.n > 0)
    : [];

  return (
    <article className="relative bg-white rounded-xl border border-gray-200 p-5">
      {/* Cross off the candidate — pinned to the top-left corner with a
          minimal buffer. Restore is available from the removed list. */}
      <button
        type="button"
        onClick={handleRemove}
        disabled={removing || analyzing}
        title="Remove this candidate from the tender's list (not viable). You can restore them later."
        aria-label={`Remove ${candidate.name} from the candidate list`}
        className="absolute left-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded text-sm leading-none text-gray-300 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
      >
        <span aria-hidden="true">✕</span>
      </button>

      <div className="flex items-start gap-4">
        {/* Avatar — photo if available, otherwise coloured initials */}
        {candidate.photo ? (
          <img
            src={candidate.photo}
            alt=""
            aria-hidden="true"
            className="h-12 w-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            aria-hidden="true"
            className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${avatarPalette(candidate.name)}`}
          >
            {initials(candidate.name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Name + status badge: the skill-match signal before analysis, the
              fit band after. */}
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              <Link
                to={`/v2/tenders/${tenderId}/candidates/${candidate.id}`}
                className="rounded hover:text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                {candidate.name}
              </Link>
            </h3>
            {analysis?.band ? (
              // After analysis: the fit band replaces the skill-match signal.
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0 ${BAND_STYLES[analysis.band].cls}`}
                title="Fit band from analysis (matching model §5)"
              >
                {BAND_STYLES[analysis.band].label}
              </span>
            ) : (
              // Before a band exists: show the skill-ranking signal.
              <span
                className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0 bg-green-50 text-green-700 border-green-200"
                title="Skill-proximity score"
              >
                {percent}% match
              </span>
            )}
          </div>

          {analysis ? (
            <p className="text-xs text-gray-500 mt-2">
              Analyzed · {analysis.ruled}/{analysis.total} criteria
              {pendingCount > 0 && <span className="text-amber-600"> · {pendingCount} pending</span>}
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-2 italic">
              Not yet analyzed · skill-match signal only
            </p>
          )}

          {/* Verdict breakdown — shown once analyzed; the band (above) summarises
              it. Hovering a chip lists the criteria behind its count (lazy-loaded). */}
          {verdictChips.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {verdictChips.map((v) => {
                const items = detail
                  ? detail.criteria.filter((c) => c.verdict === v.verdict)
                  : null;
                return (
                  <li key={v.key} className="group relative">
                    <button
                      type="button"
                      onMouseEnter={ensureDetail}
                      onFocus={ensureDetail}
                      aria-label={`${v.n} ${v.label} — show criteria`}
                      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${v.cls}`}
                    >
                      {v.n} {v.label}
                    </button>
                    {/* Tooltip — visible on hover/focus; content fills in once the
                        per-criterion detail has loaded. */}
                    <div
                      role="tooltip"
                      className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-72 max-w-[90vw] rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg group-hover:block group-focus-within:block"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                        {v.n} {v.label}
                      </p>
                      {items === null ? (
                        <p className="text-xs text-gray-400 italic">Loading…</p>
                      ) : items.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No criteria.</p>
                      ) : (
                        <ul className="space-y-1">
                          {items.map((c) => (
                            <li key={c.criterion_id} className="flex gap-1.5 text-xs text-gray-700">
                              <span aria-hidden="true" className="text-gray-300">•</span>
                              <span className="min-w-0">{c.title}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Matched skill chips */}
          {matched.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {visibleSkills.map((skill) => (
                <li key={skill}>
                  <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                    {skill}
                  </span>
                </li>
              ))}
              {!expanded && hiddenCount > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="inline-block bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-xs px-2 py-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    aria-label={`Show ${hiddenCount} more matched skills`}
                  >
                    +{hiddenCount} more
                  </button>
                </li>
              )}
              {expanded && matched.length > TOP_SKILLS_VISIBLE && (
                <li>
                  <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="inline-block bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-xs px-2 py-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    aria-label="Collapse skill list"
                  >
                    Show less
                  </button>
                </li>
              )}
            </ul>
          )}

          {/* Run / re-analyze — fires the async run, then polls to completion;
              the label tracks ruled/total while the backend works. Disabled
              once analyzed with nothing left pending. */}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={analyzing || (analysis != null && pendingCount === 0)}
              className="flex-1 inline-flex items-center justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium px-3 py-2 gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              aria-label={analyzing ? 'Analysing candidate' : analysis ? 'Re-analyze candidate' : 'Run analysis'}
            >
              <span aria-hidden="true" className={analyzing ? 'animate-spin' : ''}>
                {analyzing ? '⟳' : '▶'}
              </span>
              <span>
                {analyzing
                  ? progress && progress.total > 0
                    ? `Analysing… ${progress.ruled}/${progress.total}`
                    : 'Analysing…'
                  : analyzeError
                    ? 'Retry analysis'
                    : analysis
                      ? 'Re-analyze'
                      : 'Run analysis'}
              </span>
            </button>
            {analysis && (
              <Link
                to={`/v2/tenders/${tenderId}/candidates/${candidate.id}`}
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                View analysis
                <span aria-hidden="true">→</span>
              </Link>
            )}
          </div>
          {analyzeError && (
            <p className="mt-2 text-xs text-red-600" role="alert">
              Analysis failed. Please try again.
            </p>
          )}

          {team && (
            <TeamSeatControls tenderId={tenderId} employeeId={candidate.id} team={team} />
          )}
        </div>
      </div>
    </article>
  );
}
